import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';
import { useTable, useExpanded, useFlexLayout, usePagination, useSortBy } from 'react-table';
import classNames from 'classnames';
import { Form } from 'react-bootstrap';
import { Waypoint } from 'react-waypoint';
import { useUpdateEffect } from 'react-use';

import { useAppSelector } from 'hooks';
import { arrowDown, firstPage, arrow, emptyAlerts } from 'assets';
import { SpinnerIcon } from 'assets/icons';
import { Loading } from 'components/Fetch';
import { Dropdown } from 'components/Forms';
import { useRowSelect } from 'hooks';
import { getDisclaimerSize } from 'store/groups/reducer';

interface TableProps {
  currentPage?: number;
  enableExpandAll?: boolean;
  expandAllLabel?: string;
  data: any;
  columns: any;
  fetchData?: any;
  loading?: any;
  pageCount?: any;
  infiniteScroll?: boolean;
  leftComponent?: any;
  bottomComponent?: any;
  pagination?: boolean;
  defaultPageSize?: number;
  disablePageSizeSelect?: boolean;
  autoHeight?: boolean;
  maxHeight?: number;
  hideHeader?: boolean;
  noDataTxt?: string;
  useSelect?: boolean;
  selectTestIdentifier?: string;
  onRowClicked?: (row: any) => void;
  isRowSelectDisabled?: (row: any) => boolean;
  disableUserRowSelect?: boolean;
  onSelectChange?: (array: any) => any;
  hideMargin?: boolean;
  setPage?: (page: number) => any;
  setLimit?: (limit: number) => any;
  stickyCols?: number;
  stickyColsRight?: number;
  fullWidth?: boolean;
  onAllRowsSelectionChange?: (isAllRowsSelected: boolean) => void;
  setAllNotSelected?: boolean;
  setAllSelected?: boolean;
  truncateColumns?: string[];
  topAlignment?: boolean;
  showNewDataNotice?: boolean;
  manualPagination?: boolean;
  dataTestIdPrefix?: string;
  allowDropdownBottom?: boolean;
  showPaginationIfMultiplePage?: boolean;
  defaultSortColumn?: any;
  onSortChange?: (sort: any) => void;
  customExpandComponent?: any;
  extraClassNames?: string;
  scrollToKey?: string;
}

const Table = ({
  manualPagination = true,
  enableExpandAll,
  expandAllLabel,
  data,
  columns,
  loading,
  leftComponent,
  bottomComponent,
  pagination,
  defaultPageSize = 25,
  disablePageSizeSelect,
  infiniteScroll,
  pageCount,
  autoHeight,
  maxHeight,
  noDataTxt,
  onRowClicked,
  isRowSelectDisabled,
  disableUserRowSelect,
  currentPage = 0,
  hideHeader,
  onSelectChange,
  useSelect,
  selectTestIdentifier,
  hideMargin,
  setLimit,
  setPage,
  stickyCols = 0, // unless set in props, no column is sticky
  stickyColsRight = 0,
  fullWidth = true,
  onAllRowsSelectionChange,
  setAllNotSelected,
  setAllSelected,
  truncateColumns,
  topAlignment = false,
  fetchData,
  showNewDataNotice = false,
  dataTestIdPrefix,
  allowDropdownBottom,
  showPaginationIfMultiplePage,
  defaultSortColumn,
  onSortChange,
  customExpandComponent,
  extraClassNames,
  scrollToKey
}: TableProps) => {
  const intl = useIntl();
  const [height, setHeight] = useState(0);
  const [expandAll, setExpandAll] = useState(false);
  const disclaimerSize = useAppSelector(getDisclaimerSize);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const defaultColumn = {
    width: 175
  };
  const rowsOptions = [
    { label: '25', value: '25' },
    { label: '50', value: '50' },
    { label: '100', value: '100' }
  ];
  const {
    toggleAllRowsExpanded,
    headerGroups,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    selectedFlatRows,
    toggleAllRowsSelected,
    isAllRowsSelected,
    state: { pageIndex, pageSize, sortBy },
    page,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      initialState: {
        pageIndex: currentPage,
        pageSize: defaultPageSize,
        sortBy: defaultSortColumn ? [defaultSortColumn] : []
      },
      manualPagination,
      pageCount,
      autoResetSortBy: false,
      useControlledState: (state) => {
        return useMemo(
          () => ({
            ...state,
            pageIndex: manualPagination ? currentPage : state.pageIndex
          }),
          [state]
        );
      }
    },
    useSortBy,
    useExpanded,
    useFlexLayout,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.isRowSelectDisabled = (row: any) => {
        return isRowSelectDisabled && isRowSelectDisabled(row);
      };
      if (useSelect) {
        hooks.visibleColumns.push((columns) => [
          {
            id: 'selection',
            Header: ({ getToggleAllRowsSelectedProps }) => {
              return (
                <IndeterminateCheckbox
                  data-test="selectAllCheckbox"
                  {...getToggleAllRowsSelectedProps()}
                  disabled={disableUserRowSelect}
                />
              );
            },
            Cell: ({ row }: { row: any }) => {
              return (
                (!row.subRows?.length || (row.subRows?.length && !row.isExpanded)) && (
                  <IndeterminateCheckbox
                    {...row.getToggleRowSelectedProps()}
                    disabled={
                      disableUserRowSelect || (isRowSelectDisabled && isRowSelectDisabled(row))
                    }
                    data-test={
                      selectTestIdentifier
                        ? `select-row-${row.original[selectTestIdentifier]}`
                        : null
                    }
                  />
                )
              );
            },
            width: 50,
            disableSortBy: true
          },
          ...columns
        ]);
      }
    }
  );

  useUpdateEffect(() => {
    if (onAllRowsSelectionChange) {
      onAllRowsSelectionChange(isAllRowsSelected);
    }
  }, [isAllRowsSelected]);

  useUpdateEffect(() => {
    toggleAllRowsSelected(false);
  }, [setAllNotSelected]);

  useUpdateEffect(() => {
    toggleAllRowsSelected(true);
  }, [setAllSelected]);

  useUpdateEffect(() => {
    toggleAllRowsExpanded(expandAll);
  }, [expandAll, loading]);

  useUpdateEffect(() => {
    if (onSortChange && sortBy[0] !== defaultSortColumn) onSortChange(sortBy);
  }, [sortBy]);

  useEffect(() => {
    if (autoHeight) {
      const handleTableHeight = () => {
        // will need a way to compute this in rems for responsive designs
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;
        const disclaimerWidth = windowWidth >= 1200 ? 50 : 41; // 3 rem + 1px
        const disclaimerOffset = disclaimerWidth * disclaimerSize;
        const offsetTop =
          (document.querySelector('.table') as HTMLElement)?.parentElement?.offsetTop || 0;
        let offsetBottom = windowWidth >= 1200 ? 250 : 230; // eyeballed it
        const offsetBottomAdjustment = windowWidth >= 1200 ? 60 : 48.75; // 3.75 rem
        if (selectedFlatRows.length > 0) offsetBottom += offsetBottomAdjustment;
        const newHeight = windowHeight - offsetTop - disclaimerOffset - offsetBottom;
        setHeight(newHeight);
      };
      handleTableHeight();
      window.addEventListener('resize', handleTableHeight);
      return () => {
        // remove resize listener
        return window.removeEventListener('resize', handleTableHeight);
      };
    }
  });

  useEffect(() => {
    if (stickyCols) {
      document.querySelectorAll('.table .tr .td.sticky').forEach((el) => {
        (el as HTMLElement).style.height = `${el.parentElement?.clientHeight}px`;
      });
    }
  }, [stickyCols, data]);

  useUpdateEffect(() => {
    if (onSelectChange && !loading) {
      onSelectChange(selectedFlatRows.map((row) => row.original) || []); // reset when page reloads or changes
    }
  }, [selectedFlatRows.length]);

  const getTextAlignmentClass = (alignObj: any) => {
    return `text-align-${alignObj.align || 'left'}`;
  };

  const getHeaderAlignmentClass = (alignObj: any) => {
    switch (alignObj.align) {
      case 'center':
        return 'justify-content-center';
      case 'right':
        return 'justify-content-end';
      default:
        return 'justify-content-start';
    }
  };

  interface IRowProps {
    [x: string]: any;
    rowId?: string;
  }

  const Row = ({ rowId, ...restProps }: IRowProps) => {
    const scrollCallback = useCallback(
      (node: any) => {
        if (node && scrollToKey && rowId === scrollToKey) {
          node.scrollIntoView({ block: 'center' });
        }
      },
      [rowId]
    );

    return (
      <div {...restProps} ref={scrollCallback}>
        {restProps.children}
      </div>
    );
  };

  const renderHeader = () =>
    headerGroups.map((headerGroup) => {
      const { key: headerGroupKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
      return (
        <div key={headerGroupKey} className="tr" {...headerGroupProps}>
          {headerGroup.headers.map((column: any, index) => {
            if (column.hide) return false;
            const { key: headerKey, ...headerProps } = column.getHeaderProps({
              className: classNames('th', column.className, getTextAlignmentClass(column), {
                sticky: index < stickyCols || index >= headerGroup.headers.length - stickyColsRight,
                stickyRight: index >= headerGroup.headers.length - stickyColsRight,
                sort: column.sort || !manualPagination,
                desc: column.order === 'desc' || (column.isSorted && column.isSortedDesc),
                asc: column.order === 'asc' || (column.isSorted && !column.isSortedDesc)
              }),
              style: {
                ...column.style,
                left: index < stickyCols ? column.totalLeft : ''
              },
              onClick: column.sort,
              ...(manualPagination === false ? column.getSortByToggleProps() : undefined)
            });
            return (
              <div key={headerKey} data-test={column.id} {...headerProps}>
                <div
                  className={classNames(
                    'w-100',
                    'd-flex',
                    getHeaderAlignmentClass(column),
                    'align-items-start'
                  )}
                >
                  <div className="m-0">{column.render('Header')}</div>
                  {(column.sort || (!manualPagination && !column.disableSortBy)) && (
                    <img className="m-2" src={arrowDown} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    });

  const renderRow = () => {
    return page.map((row, rowIndex) => {
      prepareRow(row);
      const { key, ...rowProps } = row.getRowProps();
      const hasSubRow = key.toString().search(/\./);
      const isSubRow = hasSubRow >= 0;
      return (
        <React.Fragment key={key}>
          <Row
            data-test-index={rowIndex}
            data-test={`${dataTestIdPrefix}-row-${rowIndex}`}
            className={classNames('tr', {
              subrow: isSubRow,
              'cursor-pointer': onRowClicked,
              fullWidth
            })}
            rowId={(row.original as any).rowId}
            {...rowProps}
            onClick={(e: any) => {
              if (e.target?.parentNode.classList.contains('select-row')) {
                return false;
              } else if (row.canExpand && e.target?.classList.contains('arrow-dropdown')) {
                row.toggleRowExpanded(!row.isExpanded);
              } else {
                onRowClicked && onRowClicked(row.original);
              }
            }}
          >
            {row.cells.map((cell: any, index) => {
              if (cell.column.hide) return false;
              const highlightRow =
                scrollToKey && (row.original as any).rowId === scrollToKey && index === 0;
              const stickyRight = index >= row.cells.length - stickyColsRight;
              const { key: cellKey, ...cellProps } = cell.getCellProps({
                className: classNames('td', getTextAlignmentClass(cell.column), {
                  sticky: index < stickyCols || index >= row.cells.length - stickyColsRight,
                  stickyRight,
                  gradient: index + 1 === stickyCols,
                  gradientRight: index >= row.cells.length - stickyColsRight,
                  noPadding: cell.column.noPadding,
                  noVerticalPadding: cell.column.noVerticalPadding,
                  topAlignment: topAlignment
                }),
                style: {
                  left: index < stickyCols ? cell.column.totalLeft : '',
                  minHeight: '47px',
                  ...(stickyRight && { zIndex: defaultPageSize - rowIndex })
                }
              });
              return (
                <React.Fragment key={cellKey}>
                  {highlightRow && <div className="left-highlight" />}
                  <div
                    data-test={`${dataTestIdPrefix ? dataTestIdPrefix + '-' : ''}table-cell-${
                      cell.column.id
                    }-row-${rowIndex}`}
                    {...cellProps}
                  >
                    {cell.column.hideIfSubrow && isSubRow ? (
                      ''
                    ) : (
                      <div
                        className={`w-100 ${
                          truncateColumns?.includes(cell.column.Header) ? 'text-truncate' : ''
                        }`}
                      >
                        {cell.render('Cell')}
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </Row>
          {row.isExpanded && customExpandComponent ? customExpandComponent(row) : null}
        </React.Fragment>
      );
    });
  };

  const setTableLimit = (limit: number) => {
    setPageSize(limit);
    handleGotoPage(0);
  };

  const handleSetLimit = (limit: number) => (setLimit ? setLimit(limit) : setTableLimit(limit));
  const handleGotoPage = (page: number) => (setPage ? setPage(page) : gotoPage(page));
  const handleNextPage = () => (canNextPage && setPage ? setPage(currentPage + 1) : nextPage());
  const handlePreviousPage = () => (setPage ? setPage(currentPage - 1) : previousPage());

  let tbodyMaxHeight: string | number = 'none';
  if (maxHeight) {
    tbodyMaxHeight = maxHeight;
  } else if (autoHeight && data.length) {
    tbodyMaxHeight = height;
  }

  let tbodyMinHeight: string | number = 'none';
  if (data.length > 0) {
    tbodyMinHeight = 250;
  } else {
    tbodyMinHeight = 0;
  }

  useEffect(() => {
    if (!data.length && scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0;
    }
  }, [data]);

  return (
    <div
      className={`pt-2 position-relative ${hideMargin ? '' : 'mb-fixed-footer'} ${extraClassNames ? extraClassNames : ''}`}
      data-test={`${dataTestIdPrefix}-table`}
    >
      {(pagination || leftComponent) && (
        <div
          className={`d-flex mb-2 ${
            leftComponent ? 'justify-content-between' : 'justify-content-end'
          }`}
        >
          {leftComponent}
          {pagination && (showPaginationIfMultiplePage ? pageOptions.length > pageSize : true) && (
            <div className="pagination text-nowrap mb-2" data-test="table-pagination">
              {!disablePageSizeSelect && (
                <div className="d-flex align-items-center">
                  <p className="d-inline-flex m-0">
                    {intl.formatMessage({ id: 'rowsPerPageKey' })}
                  </p>{' '}
                  <Dropdown
                    dataTestId={`${
                      dataTestIdPrefix ? dataTestIdPrefix + '-' : ''
                    }table-pagination-dropdown`}
                    variant="form"
                    minWidth={80}
                    className="d-inline-flex ms-3 me-3"
                    options={rowsOptions}
                    value={rowsOptions.find(
                      (item) =>
                        item.value === (manualPagination ? defaultPageSize : pageSize).toString()
                    )}
                    onChange={(option: ISelectOption) => {
                      handleSetLimit(Number(option.value));
                    }}
                    limitHeight
                  />
                </div>
              )}
              <p className="d-flex-inline align-self-center m-0 ms-3 me-3">
                {intl.formatMessage({ id: 'pageKey' })}{' '}
                <strong data-test="page-count">
                  {pageIndex + 1}
                  {intl.formatMessage({ id: 'ofKey' })}
                  {pageOptions.length || '-'}{' '}
                </strong>{' '}
              </p>
              <div className="controls align-items-center">
                <div
                  onClick={() => handleGotoPage(0)}
                  className={classNames({ disabled: !canPreviousPage })}
                  data-test="first-page-btn"
                >
                  <img src={firstPage} />
                </div>
                <div
                  onClick={handlePreviousPage}
                  className={classNames('prev', { disabled: !canPreviousPage })}
                  data-test={`${dataTestIdPrefix ? dataTestIdPrefix + '-' : ''}previous-page-btn`}
                >
                  <img src={arrow} />
                </div>
                <div
                  onClick={handleNextPage}
                  className={classNames('next', { disabled: !canNextPage })}
                  data-test={`${dataTestIdPrefix ? dataTestIdPrefix + '-' : ''}next-page-btn`}
                >
                  <img src={arrow} />
                </div>
                <div
                  onClick={() => handleGotoPage(pageOptions.length - 1)}
                  className={classNames('last', { disabled: !canNextPage })}
                  data-test="last-page-btn"
                >
                  <img src={firstPage} />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {bottomComponent && <div className="mb-2">{bottomComponent}</div>}
      {enableExpandAll ? (
        <div className="float-right mb-3 me-3">
          <Form>
            <Form.Check
              type="switch"
              label={expandAllLabel}
              id="table-expand-all"
              data-test="table-expand-all"
              checked={expandAll}
              onChange={() => setExpandAll(!expandAll)}
            />
          </Form>
        </div>
      ) : null}
      <div
        ref={scrollContainerRef}
        className={classNames(
          `table table-responsive sticky ${
            allowDropdownBottom ? 'overflow-dropdown' : ''
          } ${!data.length ? 'overflow-x-hidden' : 'overflow-scroll'}`
        )}
      >
        {hideHeader ? null : (
          <div
            className="thead text-align-left"
            data-test={`${dataTestIdPrefix ? dataTestIdPrefix + '-' : ''}sortableTableHeader`}
          >
            {renderHeader()}
          </div>
        )}
        <div
          className="tbody"
          data-test="sortableTableBody"
          style={{ maxHeight: tbodyMaxHeight, minHeight: tbodyMinHeight }}
        >
          {loading || (infiniteScroll && loading && pageIndex === 0) ? (
            <div data-test="table-loading">
              <Loading
                className="mt-5 mb-5"
                dataTestId={`${dataTestIdPrefix ? `${dataTestIdPrefix}-` : ''}table-loading`}
              />
            </div>
          ) : (
            <div className={`${!data.length ? 'min-height-inherit' : ''} d-flex flex-col w-100`}>
              {!data.length && !loading && (
                <div
                  className="text-center p-3 d-flex flex-column align-items-center justify-content-center w-100"
                  data-test={`${dataTestIdPrefix ? dataTestIdPrefix + '-' : ''}table-no-data`}
                >
                  <img className="max-h-120" src={emptyAlerts} />
                  <h6 className="mt-4">
                    {noDataTxt || intl.formatMessage({ id: 'noDataForPeriodForReport' })}
                  </h6>
                </div>
              )}
              {showNewDataNotice && (
                <div data-test="new-data-notice" className="tr notify" onClick={fetchData}>
                  <div>{intl.formatMessage({ id: 'notificationsTableNotice' })}</div>
                  <div className="ms-2">
                    <SpinnerIcon className="icon" style={{ fontSize: '14px' }} />
                  </div>
                </div>
              )}
              {renderRow()}
              {infiniteScroll && (
                <>
                  <Waypoint onEnter={handleNextPage} />
                  {loading && pageIndex !== 0 && <Loading className="mt-5 mb-5" />}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const IndeterminateCheckbox = forwardRef(({ indeterminate, ...rest }: any, ref: any) => {
  const defaultRef = useRef();
  const resolvedRef = ref || defaultRef;

  useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);

  return <Form.Check type="checkbox" className="select-row" ref={resolvedRef} {...rest} />;
});

export default Table;
